import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Utensils, Plus, X, Banknote, CreditCard, NotebookPen, Trash2, ChefHat, PackageCheck, Check, User, Users, Wallet } from "lucide-react";
import { MenuPicker } from "../../components/MenuPicker";

const money = (v) => `₺${Number(v || 0).toFixed(2)}`;
const ST = {
  pending: { label: "Bekliyor", cls: "text-amber-400 border-amber-500/30 bg-amber-950/30" },
  preparing: { label: "Hazırlanıyor", cls: "text-blue-400 border-blue-500/30 bg-blue-950/30" },
  delivered: { label: "Teslim", cls: "text-emerald-400 border-emerald-500/30 bg-emerald-950/30" },
};

export default function Tables() {
  const [tables, setTables] = useState([]);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/tables"); setTables(data); } catch {}
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const groups = ["İç", "Bahçe"];
  return (
    <div>
      <h1 className="font-serif text-3xl sm:text-4xl text-white mb-1">Masalar & Adisyon</h1>
      <p className="text-zinc-400 text-sm mb-6">Masaya hesap açın, sipariş girin, hazırlayıp teslim edin ve ödemeyi alın</p>

      {groups.map((g) => (
        <div key={g} className="mb-8">
          <h2 className="font-serif text-xl text-[#FFE599] mb-3">{g === "İç" ? "İç Mekan" : "Bahçe"}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {tables.filter((t) => t.group === g).map((t) => (
              <button key={t.name} onClick={() => setActive(t.name)} data-testid={`table-${t.name}`}
                className={`card-lux card-hover p-4 text-left ${t.open ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" : ""}`}>
                <div className="flex items-center justify-between">
                  <span className="font-serif text-lg text-white">{t.name}</span>
                  <Utensils size={16} className={t.open ? "text-[#D4AF37]" : "text-zinc-600"} />
                </div>
                {t.open ? (
                  <div className="mt-2">
                    <div className="text-[10px] font-mono uppercase text-emerald-400">Dolu • {t.itemCount} kalem</div>
                    <div className="font-mono text-[#FFE599] mt-1">{money(t.total)}</div>
                    {t.remaining > 0 && <div className="text-[10px] text-amber-400 font-mono">Kalan: {money(t.remaining)}</div>}
                  </div>
                ) : <div className="mt-2 text-[10px] font-mono uppercase text-zinc-600">Boş</div>}
              </button>
            ))}
          </div>
        </div>
      ))}

      {active && <TableDetail name={active} onClose={() => { setActive(null); load(); }} onChange={load} />}
    </div>
  );
}

function TableDetail({ name, onClose, onChange }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [picker, setPicker] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState({});
  const [person, setPerson] = useState("");
  const [sel, setSel] = useState(new Set());
  const [method, setMethod] = useState("cash");
  const [custName, setCustName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/tables/${encodeURIComponent(name)}`); setD(data); } catch {}
  }, [name]);
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.isAvailable)));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [load]);
  useEffect(() => { const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const items = d?.items || [];
  const unpaid = items.filter((i) => !i.paid);
  const selItems = unpaid.filter((i) => sel.has(i.lineId));
  const payTarget = selItems.length ? selItems : unpaid;
  const payAmount = payTarget.reduce((s, i) => s + i.price * i.qty, 0);

  const personSuggestions = useMemo(() => [...new Set(items.map((i) => (i.person || "").trim()).filter(Boolean))], [items]);

  // Group items by person (named first, "Masa / Ortak" last)
  const groups = useMemo(() => {
    const m = new Map();
    items.forEach((i) => {
      const key = (i.person || "").trim() || "__none__";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(i);
    });
    const entries = [...m.entries()];
    entries.sort((a, b) => (a[0] === "__none__" ? 1 : b[0] === "__none__" ? -1 : a[0].localeCompare(b[0], "tr")));
    return entries;
  }, [items]);

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectPerson = (groupItems) => {
    const ids = groupItems.filter((i) => !i.paid).map((i) => i.lineId);
    setSel(new Set(ids));
    if (ids.length) toast.success("Bu kişinin ödenmemiş ürünleri seçildi");
  };

  const addItems = async () => {
    const list = Object.values(cart);
    if (!list.length) return toast.error("Ürün seçin");
    setBusy(true);
    try {
      await api.post(`/tables/${encodeURIComponent(name)}/items`, { items: list.map((c) => ({ productId: c.p.id, name: c.p.name, price: c.p.price, qty: c.qty, note: "", person: person.trim() })) });
      toast.success(person.trim() ? `${person.trim()} adına eklendi` : "Sipariş eklendi");
      setCart({}); setPicker(false); load(); onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setStatus = async (lineId, status) => { try { await api.patch(`/tables/${encodeURIComponent(name)}/items/${lineId}/status`, { status }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const removeItem = async (lineId) => { try { await api.delete(`/tables/${encodeURIComponent(name)}/items/${lineId}`); load(); onChange(); } catch (e) { toast.error(errMsg(e)); } };

  const pay = async () => {
    if (!payTarget.length) return toast.error("Ödenecek kalem yok");
    if (method === "credit" && !custName.trim()) return toast.error("Veresiye için müşteri adı girin");
    setBusy(true);
    try {
      await api.post(`/tables/${encodeURIComponent(name)}/pay`, { method, lineIds: selItems.length ? [...sel] : [], customerName: custName });
      toast.success(`Ödeme alındı: ${money(payAmount)} (${method === "cash" ? "Nakit" : method === "card" ? "Kart" : "Veresiye"})`);
      setSel(new Set()); setCustName("");
      const { data } = await api.get(`/tables/${encodeURIComponent(name)}`);
      setD(data); onChange();
      if (data.status === "empty") { toast.success("Hesap kapandı"); onClose(); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const closeTable = async () => {
    if (!window.confirm("Hesabı kapatmak istediğinize emin misiniz?")) return;
    try { await api.post(`/tables/${encodeURIComponent(name)}/close`); toast.success("Hesap kapatıldı"); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };

  const addToCart = (p) => setCart((c) => ({ ...c, [p.id]: { p, qty: (c[p.id]?.qty || 0) + 1 } }));
  const subFromCart = (p) => setCart((c) => { const q = (c[p.id]?.qty || 0) - 1; const n = { ...c }; if (q <= 0) delete n[p.id]; else n[p.id] = { p, qty: q }; return n; });

  const renderItem = (i) => (
    <div key={i.lineId} className={`bg-[#141418] rounded-lg p-2.5 ${i.paid ? "opacity-60" : ""}`} data-testid={`titem-${i.lineId}`}>
      <div className="flex items-center gap-3">
        {!i.paid && <input type="checkbox" checked={sel.has(i.lineId)} onChange={() => toggleSel(i.lineId)} className="accent-[#D4AF37] w-4 h-4" data-testid={`sel-${i.lineId}`} />}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white">{i.qty}x {i.name}</div>
          <div className="font-mono text-xs text-[#FFE599]">{money(i.price * i.qty)}</div>
        </div>
        {i.paid ? <span className="text-[10px] font-mono px-2 py-1 rounded-full text-emerald-400 border border-emerald-500/30 bg-emerald-950/30 flex items-center gap-1"><Check size={11} />Ödendi</span>
          : <span className={`text-[10px] font-mono px-2 py-1 rounded-full border ${ST[i.status].cls}`}>{ST[i.status].label}</span>}
      </div>
      {!i.paid && (
        <div className="flex gap-1.5 mt-2 justify-end">
          {i.status === "pending" && <button onClick={() => setStatus(i.lineId, "preparing")} className="btn-outline text-xs px-2.5 py-1 rounded-lg flex items-center gap-1" data-testid={`prep-${i.lineId}`}><ChefHat size={12} />Hazırla</button>}
          {i.status === "preparing" && <button onClick={() => setStatus(i.lineId, "delivered")} className="btn-gold text-xs px-2.5 py-1 rounded-lg flex items-center gap-1" data-testid={`deliver-${i.lineId}`}><PackageCheck size={12} />Teslim Et</button>}
          <button onClick={() => removeItem(i.lineId)} className="text-rose-400 hover:bg-rose-500/10 h-6 w-6 rounded-lg flex items-center justify-center" data-testid={`del-${i.lineId}`}><Trash2 size={13} /></button>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="table-detail">
      <div className="card-lux w-full max-w-lg max-h-[92vh] flex flex-col scale-in">
        <div className="px-5 py-4 border-b border-[#D4AF37]/20 flex items-center justify-between">
          <div>
            <h2 className="font-serif text-2xl gold-text">{name}</h2>
            {d && <p className="text-xs text-zinc-400 mt-0.5">Toplam {money(d.total)} • Ödenen {money(d.paid)} • <span className="text-amber-400">Kalan {money(d.remaining)}</span></p>}
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="table-detail-close"><X size={24} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 no-scrollbar">
          {items.length === 0 && <p className="text-center text-zinc-500 py-6">Bu masada henüz sipariş yok. Aşağıdan ürün ekleyin.</p>}

          {groups.map(([key, gItems]) => {
            const gUnpaid = gItems.filter((i) => !i.paid);
            const gUnpaidTotal = gUnpaid.reduce((s, i) => s + i.price * i.qty, 0);
            const isNone = key === "__none__";
            return (
              <div key={key} className="space-y-2" data-testid={`person-group-${isNone ? "ortak" : key}`}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm font-serif text-[#FFE599]">
                    {isNone ? <Users size={14} /> : <User size={14} />}{isNone ? "Masa / Ortak" : key}
                  </span>
                  {gUnpaid.length > 0 && (
                    <button onClick={() => selectPerson(gItems)} className="btn-outline text-[11px] px-2.5 py-1 rounded-lg flex items-center gap-1" data-testid={`pay-person-${isNone ? "ortak" : key}`}>
                      <Wallet size={12} />{money(gUnpaidTotal)} • Öde
                    </button>
                  )}
                </div>
                <div className="space-y-1.5 pl-1">{gItems.map(renderItem)}</div>
              </div>
            );
          })}

          {picker && (
            <div className="card-lux p-3 space-y-2.5" data-testid="table-picker">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Kim için? (opsiyonel)</label>
                <input list="person-suggestions" value={person} onChange={(e) => setPerson(e.target.value)} placeholder="Ör. Ahmet — boş bırakılırsa Masa/Ortak" className="lux-input py-2 text-sm mt-1.5" data-testid="table-person-input" />
                <datalist id="person-suggestions">
                  {personSuggestions.map((p) => <option key={p} value={p} />)}
                </datalist>
              </div>
              <MenuPicker products={products} categories={categories} cart={cart} onAdd={addToCart} onSub={subFromCart} maxListHeight="max-h-52" />
              <button onClick={addItems} disabled={busy} className="btn-gold w-full py-2.5 rounded-xl text-sm" data-testid="add-items-btn">{person.trim() ? `${person.trim()} Adına Ekle` : "Seçilenleri Ekle"}</button>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-[#D4AF37]/20 space-y-3">
          {!picker && <button onClick={() => { setPicker(true); setPerson(""); }} className="btn-outline w-full py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm" data-testid="open-picker-btn"><Plus size={16} />Ürün / Sipariş Ekle</button>}

          {unpaid.length > 0 && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-400">{selItems.length ? `Seçili ${selItems.length} kalem` : "Tüm kalan hesap"} • <span className="text-[#FFE599] font-mono">{money(payAmount)}</span>{selItems.length ? <button onClick={() => setSel(new Set())} className="ml-2 text-zinc-500 hover:text-white underline" data-testid="clear-sel">seçimi temizle</button> : null}</div>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setMethod("cash")} className={`py-2.5 rounded-xl text-xs flex flex-col items-center gap-1 ${method === "cash" ? "btn-gold" : "btn-outline"}`} data-testid="m-cash"><Banknote size={16} />Nakit</button>
                <button onClick={() => setMethod("card")} className={`py-2.5 rounded-xl text-xs flex flex-col items-center gap-1 ${method === "card" ? "btn-gold" : "btn-outline"}`} data-testid="m-card"><CreditCard size={16} />Kart</button>
                <button onClick={() => setMethod("credit")} className={`py-2.5 rounded-xl text-xs flex flex-col items-center gap-1 ${method === "credit" ? "btn-gold" : "btn-outline"}`} data-testid="m-credit"><NotebookPen size={16} />Veresiye</button>
              </div>
              {method === "credit" && <input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="Veresiye — müşteri adı" className="lux-input" data-testid="credit-name" />}
              <button onClick={pay} disabled={busy} className="btn-gold w-full py-3 rounded-xl" data-testid="pay-btn">Ödeme Al ({money(payAmount)})</button>
            </div>
          )}
          {items.length > 0 && <button onClick={closeTable} className="w-full py-2 text-xs text-zinc-400 hover:text-rose-400" data-testid="close-table-btn">Hesabı Kapat (boşalt)</button>}
        </div>
      </div>
    </div>
  );
}
